import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { UserCheck, Users, Loader2, Search, Check, CalendarClock, ChevronDown } from 'lucide-react';
import { reportsAPI } from '../lib/api';
import { toast } from 'sonner';

const AVATAR_COLORS = [
  'bg-teal-100 text-teal-700',
  'bg-blue-100 text-blue-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-emerald-100 text-emerald-700',
  'bg-indigo-100 text-indigo-700',
];

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';

const colorFor = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
};

export const OnShiftTodayCard = ({ locationId, locationName, date }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [onShiftIds, setOnShiftIds] = useState([]);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const res = await reportsAPI.getOnShiftToday(date, locationId || undefined);
      setEmployees(res.data.employees || []);
      setOnShiftIds(res.data.on_shift_ids || []);
      setSelected(new Set(res.data.on_shift_ids || []));
      setUpdatedBy(res.data.updated_by_name || null);
    } catch (e) {
      toast.error('Failed to load shift');
    } finally {
      setLoading(false);
    }
  }, [date, locationId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const ids = Array.from(selected);
      await reportsAPI.setOnShiftToday({ date, location_id: locationId || null, employee_ids: ids });
      setOnShiftIds(ids);
      toast.success("Today's shift saved");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save shift');
    } finally {
      setSaving(false);
    }
  };

  const onShiftEmployees = employees.filter(e => onShiftIds.includes(e.id));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e => (e.full_name || '').toLowerCase().includes(q) || (e.role || '').toLowerCase().includes(q));
  }, [employees, search]);

  return (
    <>
      <Card className="border-0 shadow-sm bg-white overflow-hidden" data-testid="on-shift-today-card">
        {/* White header with a green/red accent line (click to expand/collapse). Red when no one is on shift. */}
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className={`w-full bg-white px-5 py-4 flex items-center justify-between text-left border-t-4 border-l-4 ${onShiftEmployees.length === 0 ? 'border-red-500' : 'border-teal-500'}`}
          data-testid="on-shift-toggle"
          aria-expanded={expanded}
        >
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${onShiftEmployees.length === 0 ? 'bg-red-50' : 'bg-teal-50'}`}>
              <UserCheck className={`w-5 h-5 ${onShiftEmployees.length === 0 ? 'text-red-500' : 'text-teal-600'}`} />
            </div>
            <div>
              <h2 className="text-slate-800 font-semibold leading-tight">On Shift Today</h2>
              <p className="text-slate-400 text-[11px] leading-tight">{locationName || 'All Locations'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-sm font-bold ${onShiftEmployees.length === 0 ? 'bg-red-50 text-red-600' : 'bg-teal-50 text-teal-700'}`} data-testid="on-shift-count">
              {onShiftEmployees.length}
            </span>
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </button>

        <CardContent className="p-5">
          {expanded && (
            onShiftEmployees.length > 0 ? (
              <div className="space-y-2.5" data-testid="on-shift-names">
                {onShiftEmployees.map(emp => (
                  <div key={emp.id} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${colorFor(emp.id)}`}>
                      {initials(emp.full_name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate leading-tight">{emp.full_name}</p>
                      {emp.role && <p className="text-[11px] text-slate-400 capitalize leading-tight">{String(emp.role).replace(/_/g, ' ')}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center text-center py-4">
                <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-2">
                  <CalendarClock className="w-6 h-6 text-slate-300" />
                </div>
                <p className="text-sm text-slate-500">No staff set for today yet.</p>
              </div>
            )
          )}

          {!expanded && (
            <p className={`text-sm text-center ${onShiftEmployees.length === 0 ? 'text-rose-600' : 'text-slate-500'}`} data-testid="on-shift-collapsed-summary">
              {onShiftEmployees.length > 0
                ? `${onShiftEmployees.length} ${onShiftEmployees.length === 1 ? 'person' : 'people'} on shift — tap to view`
                : 'No staff set for today yet — tap to set'}
            </p>
          )}

          {expanded && (
            <Button
              className="w-full mt-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white"
              onClick={() => { setSearch(''); setSelected(new Set(onShiftIds)); setOpen(true); }}
              data-testid="set-shift-btn"
            >
              {onShiftEmployees.length > 0 ? 'Edit Shift' : 'Set Shift'}
            </Button>
          )}

          {updatedBy && expanded && (
            <p className="text-[11px] text-slate-400 mt-2.5 text-center">Last updated by {updatedBy}</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0">
          <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-5">
            <DialogHeader className="space-y-1">
              <DialogTitle className="flex items-center gap-2 text-white">
                <Users className="w-5 h-5" /> Set Today's Shift
              </DialogTitle>
              <p className="text-white/85 text-sm">
                {locationName || 'All Locations'} • Select who is working today. This pre-fills the daily closing.
              </p>
            </DialogHeader>
          </div>

          <div className="px-6 pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff by name or role..."
                className="pl-9 rounded-xl"
                data-testid="shift-search-input"
              />
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
              <span>{selected.size} selected</span>
              {filtered.length > 0 && (
                <button
                  className="text-teal-600 hover:text-teal-700 font-medium"
                  onClick={() => {
                    const allSelected = filtered.every(e => selected.has(e.id));
                    setSelected(prev => {
                      const next = new Set(prev);
                      filtered.forEach(e => { if (allSelected) next.delete(e.id); else next.add(e.id); });
                      return next;
                    });
                  }}
                  data-testid="shift-select-all-btn"
                >
                  {filtered.every(e => selected.has(e.id)) ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[45vh] overflow-y-auto px-4 py-2 mt-1">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading staff...
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">
                {employees.length === 0 ? 'No employees assigned to this location.' : 'No staff match your search.'}
              </p>
            ) : (
              filtered.map(emp => {
                const isOn = selected.has(emp.id);
                return (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => toggle(emp.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 transition-colors text-left ${isOn ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                    data-testid={`shift-emp-${emp.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${colorFor(emp.id)}`}>
                      {initials(emp.full_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{emp.full_name}</p>
                      {emp.role && <p className="text-xs text-slate-400 capitalize">{String(emp.role).replace(/_/g, ' ')}</p>}
                    </div>
                    <span
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isOn ? 'bg-teal-500 border-teal-500' : 'border-slate-300'}`}
                      data-testid={`shift-emp-checkbox-${emp.id}`}
                    >
                      {isOn && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-slate-100">
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)} data-testid="shift-cancel-btn">Cancel</Button>
            <Button className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white" onClick={save} disabled={saving || loading} data-testid="shift-save-btn">
              {saving ? 'Saving...' : `Save (${selected.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OnShiftTodayCard;
